import { isValidDollarAmount } from "@platform/plan-grid-core";

import {
  MAX_SALARY_MULTIPLIER,
  SALARY_MULTIPLIER_STEP,
  isValidSalaryMultiplier,
} from "./SalaryMultipliersField";

import type { DollarAmount, DraftDollarAmount } from "@platform/plan-grid-core";

/**
 * How a benefit-description type stores its value:
 * - `dollar`  → a single whole-dollar amount (with an optional minimum)
 * - `percent` → a single percentage 1–100 (with an optional minimum)
 * - `multiplier` → a single salary multiplier
 * - `dollarArray` → a list of whole-dollar amounts (`{amount}[]`)
 * - `multiplierArray` → a list of salary multipliers (`{amount}[]`)
 * A type absent from the value-kind map is a sentinel (e.g. Not Included) with
 * no value.
 */
export type BenefitDescriptionValueKind =
  | "dollar"
  | "percent"
  | "multiplier"
  | "dollarArray"
  | "multiplierArray";

/** Inclusive bounds on how many entries a list-valued type accepts. */
export interface BenefitDescriptionEntryLimits {
  min: number;
  max: number;
}

export interface BenefitDescriptionBehavior {
  valueKind: BenefitDescriptionValueKind;
  showMinimum: boolean;
  maxEntries: number;
}

// Each benefit-description type value fully determines how its value is edited:
// the kind of input(s), whether the optional Minimum applies, and (for the
// list types) how many amounts are allowed. A coverage whose list bounds differ
// overrides `maxEntries` through the spec's entry limits.
const TYPE_BEHAVIOR: Record<string, BenefitDescriptionBehavior> = {
  dollarAmount: { valueKind: "dollar", showMinimum: true, maxEntries: 0 },
  increments: { valueKind: "dollar", showMinimum: true, maxEntries: 0 },
  percentage: { valueKind: "percent", showMinimum: true, maxEntries: 0 },
  percentageOfEeBenefit: {
    valueKind: "percent",
    showMinimum: true,
    maxEntries: 0,
  },
  percentageOfEmployeeElection: {
    valueKind: "percent",
    showMinimum: true,
    maxEntries: 0,
  },
  salaryMultiplier: {
    valueKind: "multiplier",
    showMinimum: false,
    maxEntries: 0,
  },
  multipleDollarAmounts: {
    valueKind: "dollarArray",
    showMinimum: false,
    maxEntries: 3,
  },
  multipleSalaryMultipliers: {
    valueKind: "multiplierArray",
    showMinimum: false,
    maxEntries: 10,
  },
  flatAmount: { valueKind: "dollarArray", showMinimum: true, maxEntries: 2 },
};

const isListKind = (
  valueKind: BenefitDescriptionValueKind | undefined,
): boolean => valueKind === "dollarArray" || valueKind === "multiplierArray";

export const benefitDescriptionBehavior = (
  type: string | undefined,
): BenefitDescriptionBehavior | undefined =>
  type ? TYPE_BEHAVIOR[type] : undefined;

/** What the editor renders and enforces for the selected type. */
export interface BenefitDescriptionEditorShape {
  valueKind: BenefitDescriptionValueKind | undefined;
  showMinimum: boolean;
  maxEntries: number;
  minEntries: number;
}

export const benefitDescriptionEditorShape = (
  type: string | undefined,
  hasMinimumField: boolean,
  entryLimits?: BenefitDescriptionEntryLimits,
): BenefitDescriptionEditorShape => {
  const behavior = benefitDescriptionBehavior(type);

  return {
    valueKind: behavior?.valueKind,
    showMinimum: (behavior?.showMinimum ?? false) && hasMinimumField,
    maxEntries: entryLimits?.max ?? behavior?.maxEntries ?? 0,
    minEntries: entryLimits?.min ?? 0,
  };
};

/** Whether the current draft is invalid for its value kind, for save-time blocking. */
export const benefitDescriptionHasError = (
  valueKind: BenefitDescriptionValueKind | undefined,
  single: string,
  arrayEntries: DraftDollarAmount[],
  minEntries = 0,
): boolean => {
  if (isListKind(valueKind)) {
    const isValidEntry =
      valueKind === "multiplierArray"
        ? isValidSalaryMultiplier
        : isValidDollarAmount;

    return (
      arrayEntries.length < minEntries || !arrayEntries.every(isValidEntry)
    );
  }

  if (valueKind === "percent") {
    const value = Number(single);
    return single === "" || !(value >= 1 && value <= 100);
  }

  if (valueKind === "multiplier") {
    const value = Number(single);
    return (
      single === "" ||
      !(value >= SALARY_MULTIPLIER_STEP && value <= MAX_SALARY_MULTIPLIER)
    );
  }

  if (valueKind === "dollar") {
    const value = Number(single);
    return single === "" || !(Number.isInteger(value) && value >= 1);
  }

  return false;
};

const ENTRY_NOUN: Partial<Record<BenefitDescriptionValueKind, string>> = {
  dollarArray: "dollar amounts",
  multiplierArray: "salary multipliers",
};

/**
 * Why the current draft blocks saving. Shown whenever the value is invalid, so
 * a disabled Save button always states what it is waiting for — including
 * before the inputs themselves have been touched.
 */
export const benefitDescriptionErrorMessage = (
  valueKind: BenefitDescriptionValueKind | undefined,
  minEntries: number,
): string => {
  if (isListKind(valueKind)) {
    const noun = ENTRY_NOUN[valueKind as BenefitDescriptionValueKind];

    return minEntries > 1
      ? `Enter at least ${minEntries} ${noun}.`
      : `Enter a valid amount for every row.`;
  }

  if (valueKind === "percent") return "Enter a percentage between 1 and 100.";
  if (valueKind === "multiplier")
    return `Enter a salary multiplier between ${SALARY_MULTIPLIER_STEP} and ${MAX_SALARY_MULTIPLIER}.`;

  return "Enter a whole dollar amount of at least $1.";
};

/** Coerce a stored amount into the `{amount}[]` array shape. */
export const readDollarAmounts = (raw: unknown): DollarAmount[] => {
  if (!Array.isArray(raw)) return [];

  return raw
    .filter(
      (option): option is DollarAmount =>
        !!option &&
        typeof option === "object" &&
        typeof (option as DollarAmount).amount === "number",
    )
    .map((option) => ({ amount: option.amount }));
};

/** Coerce a stored single amount into a string for the input. */
export const readSingleAmount = (raw: unknown): string =>
  typeof raw === "number" ? String(raw) : "";

export interface BenefitDescriptionSave {
  amount: number | DollarAmount[] | null;
  minimum: number | null;
}

/** Build the grid-shape amount/minimum from the editor drafts for a type. */
export const buildBenefitDescriptionSave = (
  valueKind: BenefitDescriptionValueKind | undefined,
  single: string,
  minimum: string,
  arrayEntries: DraftDollarAmount[],
): BenefitDescriptionSave => {
  const parsedMin = minimum === "" ? null : Number(minimum);
  const min =
    parsedMin !== null && Number.isFinite(parsedMin) ? parsedMin : null;

  if (isListKind(valueKind)) {
    const amounts = arrayEntries
      .map((entry) => Number(entry.amount))
      .filter((amount) => Number.isFinite(amount) && amount > 0)
      .map((amount) => ({ amount }));

    return { amount: amounts.length > 0 ? amounts : null, minimum: min };
  }

  if (
    valueKind === "dollar" ||
    valueKind === "percent" ||
    valueKind === "multiplier"
  ) {
    const amount = single === "" ? null : Number(single);

    return {
      amount: amount !== null && Number.isFinite(amount) ? amount : null,
      minimum: min,
    };
  }

  return { amount: null, minimum: null };
};
